import React from 'react'
import { TableColumnDefinition } from '@fluentui/react-components'
import {
   UpsertProcessOwnerDto,
   Organisation,
   OrganisationSize,
   ProcessOwnerDto,
   ProtectedAssetGoal,
   ProtectedAssetGoalType,
   SNI,
   SNIWorkplaceConnection,
   Workplace,
   UserAccount,
   Role,
} from '../api/schemas/schema'
import { IDataContext } from '../interfaces/IDataContext'
import { api } from './apiHelper'
import { handleMessage } from './stateHelper'
import { formatToSwedishCrowns } from './stringHelper'
import { t } from '../i18n/i18n'
import { ProcessOwnerAvatarOutput } from '../components/common/inputs/ProcessOwnerAvatarOutput'
import { getFullName } from './accountHelper'
import { IRootState } from '../interfaces/IRootState'
import { Text } from '../components/common/text/Text'

export const enum SettingsTabs {
   ProtectedAssets = 'protectedAssets',
   OrganisationalGoals = 'organisationalGoals',
   Workplaces = 'workplaces',
   ProcessOwners = 'processOwners',
   Roles = 'roles',
}

export const getProtectedAssetGoalUsage = async (filteredItems: any[], setProtectedAssetGoalInUseMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>) => {
   const protectedAssetInUse: Record<string, boolean> = {}
   for (const pag of filteredItems) {
      protectedAssetInUse[pag.id] = (await api().isProtectedAssetGoalInUse(pag.id)).data
   }
   setProtectedAssetGoalInUseMap(protectedAssetInUse)
}

export const addProtectedAssetGoal = async (close: () => void, dataContext: IDataContext, data: ProtectedAssetGoal) => {
   try {
      const { userAccount, organisation } = dataContext.state
      data.organisationId = organisation.id
      data.modifiedBy = userAccount
      data.modifiedById = userAccount.id
      data.createdBy = userAccount
      data.createdById = userAccount.id

      const addedProtectedAsset = (await api().addProtectedAssetGoal(data)).data
      dataContext.setRootState((prev) => ({
         ...prev,
         protectedAssetGoals: [...prev.protectedAssetGoals, addedProtectedAsset],
      }))
      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullySaved', { Item: getProtectedAssetGoalName(data.type) }))
      return addedProtectedAsset
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotCreateItem', { Item: getProtectedAssetGoalName(data.type) }))
   } finally {
      close()
   }
}
export const updateProtectedAssetGoal = async (close: () => void, dataContext: IDataContext, data: ProtectedAssetGoal) => {
   try {
      const { userAccount } = dataContext.state
      data.modifiedById = userAccount.id

      const updatedProtectedAsset = (await api().updateProtectedAssetGoal(data)).data
      dataContext.setRootState((prev) => ({
         ...prev,
         protectedAssetGoals: prev.protectedAssetGoals.map((item) => (item.id === updatedProtectedAsset.id ? updatedProtectedAsset : item)),
      }))

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: getProtectedAssetGoalName(data.type) }))
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotUpdateItem', { Item: getProtectedAssetGoalName(data.type) }))
   } finally {
      close()
   }
}

export const deleteProtectedAssetGoal = async (
   protectedAssetGoalId: string,
   dataContext: IDataContext,
   setRootState: React.Dispatch<React.SetStateAction<IRootState>>
) => {
   const protectedAssetOrGoal = dataContext.state.protectedAssetGoals.find((item) => item.id === protectedAssetGoalId)
   const itemName = getProtectedAssetGoalName(protectedAssetOrGoal.type)

   try {
      await api().deleteProtectedAssetGoal(protectedAssetGoalId)

      setRootState((prev) => ({
         ...prev,
         protectedAssetGoals: prev.protectedAssetGoals.filter((item) => item.id !== protectedAssetGoalId),
      }))

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyDeleted', { Item: itemName }))
   } catch (error: any) {
      let errorMessage = t('CouldNotDeleteItem', { Item: itemName })
      switch (error.status) {
         case 400:
            errorMessage = t('NoMatchingItemFound', { Item: itemName })
            break
         case 403:
            errorMessage = t('UserNotAllowedToActionItem', { Action: t('Delete').toLowerCase(), Item: itemName.toLowerCase() })
            break
         case 409:
            errorMessage =
               protectedAssetOrGoal.type === ProtectedAssetGoalType.ProtectedAsset
                  ? t('TheProtectedAssetIsInUseAndCannotBeDeleted')
                  : t('TheOrganisationalGoalIsInUseAndCannotBeDeleted')
            break
      }
      handleMessage(dataContext.setRootState, 'error', t('CouldNotDeleteItem', { Item: itemName }), errorMessage)
   }
}

export const updateWorkplace = async (
   close: () => void,
   setAllWorkplaces: React.Dispatch<React.SetStateAction<Workplace[]>>,
   dataContext: IDataContext,
   data: Workplace
) => {
   try {
      const { userAccount, organisation } = dataContext.state
      data.modifiedById = userAccount.id

      const updatedWorkplace = (await api().updateWorkplace(organisation.id, data)).data
      setAllWorkplaces((prev) => prev.map((wp) => (wp.id === updatedWorkplace.id ? updatedWorkplace : wp)))

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('Workplace') }))
      return updatedWorkplace
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotUpdateItem', { Item: t('Workplace') }))
   } finally {
      close()
   }
}

export const addWorkplace = async (
   close: () => void,
   setAllWorkplaces: React.Dispatch<React.SetStateAction<Workplace[]>>,
   dataContext: IDataContext,
   data: Workplace
) => {
   try {
      const { userAccount, organisation } = dataContext.state
      data.organisationId = organisation.id
      data.modifiedById = userAccount.id
      data.createdById = userAccount.id

      const addedWorkplace = (await api().addWorkplace(organisation.id, data)).data
      setAllWorkplaces((prev) => [...prev, addedWorkplace])

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyCreated', { Item: t('Workplace') }))
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotCreateItem', { Item: t('Workplace') }))
   } finally {
      close()
   }
}

export const deleteWorkplace = async (workplaceId: string, dataContext: IDataContext, setAllWorkplaces: React.Dispatch<React.SetStateAction<Workplace[]>>) => {
   try {
      await api().deleteWorkplace(workplaceId)
      setAllWorkplaces((prev) => prev.filter((wp) => wp.id !== workplaceId))

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyDeleted', { Item: t('Workplace') }))
   } catch (error: any) {
      let errorMessage = t('CouldNotDeleteItem', { Item: t('Workplace') })
      switch (error.status) {
         case 400:
            errorMessage = t('NoMatchingItemFound', { Item: t('Workplace').toLowerCase() })
            break
         case 403:
            errorMessage = t('UserNotAllowedToActionItem', { Action: t('Delete').toLowerCase(), Item: t('Workplace').toLowerCase() })
            break
         case 409:
            errorMessage = t('TheWorkplaceIsInUseAndCannotBeDeleted')
            break
      }
      handleMessage(dataContext.setRootState, 'error', t('CouldNotDeleteItem', { Item: t('Workplace') }), errorMessage)
   }
}

export const updateProcessOwner = async (
   setAllProcessOwners: React.Dispatch<React.SetStateAction<ProcessOwnerDto[]>>,
   dataContext: IDataContext,
   data: UpsertProcessOwnerDto,
   processOwnerId: string
) => {
   try {
      const { userAccount, organisation } = dataContext.state
      data.modifiedById = userAccount.id
      data.organisationId = organisation.id
      if (data.userAccount) data.userAccountId = data.userAccount.id
      const updatedProcessOwner = (await api().updateProcessOwner(processOwnerId, data)).data
      setAllProcessOwners((prev) => prev.map((p) => (p.id === updatedProcessOwner.id ? updatedProcessOwner : p)))

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('ProcessOwner') }))
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotUpdateItem', { Item: t('ProcessOwner') }))
   }
}
export const addProcessOwner = async (
   setAllProcessOwners: React.Dispatch<React.SetStateAction<ProcessOwnerDto[]>>,
   dataContext: IDataContext,
   data: UpsertProcessOwnerDto
) => {
   try {
      const { userAccount, organisation } = dataContext.state
      data.organisationId = organisation.id
      data.modifiedById = userAccount.id
      data.createdById = userAccount.id

      const addedProcessOwner = (await api().addProcessOwner(data)).data
      setAllProcessOwners((prev) => [...prev, addedProcessOwner])

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyCreated', { Item: t('ProcessOwner') }))
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotCreateItem', { Item: t('ProcessOwner') }))
   }
}

export const deleteProcessOwner = async (processOwnerId: string, dataContext: IDataContext, setAllProcessOwners: React.Dispatch<React.SetStateAction<ProcessOwnerDto[]>>) => {
   try {
      await api().deleteProcessOwner(processOwnerId)
      setAllProcessOwners((prev) => prev.filter((po) => po.id !== processOwnerId))

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyDeleted', { Item: t('ProcessOwner') }))
   } catch (error: any) {
      let errorMessage = t('CouldNotDeleteItem', { Item: t('ProcessOwner') })
      switch (error.status) {
         case 400:
            errorMessage = t('NoMatchingItemFound', { Item: t('ProcessOwner').toLowerCase() })
            break
         case 403:
            errorMessage = t('UserNotAllowedToActionItem', { Action: t('Delete').toLowerCase(), Item: t('ProcessOwner').toLowerCase() })
            break
         case 409:
            errorMessage = t('TheProcessOwnerIsInUseAndCannotBeDeleted')
            break
      }
      handleMessage(dataContext.setRootState, 'error', t('CouldNotDeleteItem', { Item: t('ProcessOwner') }), errorMessage)
   }
}


// export const getProcessOwnerUsage = async (filteredItems: any[], setProcessOwnersInUseMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>) => {
//    const processOwnersInUse: Record<string, boolean> = {}
//    for (const po of filteredItems) {
//       processOwnersInUse[po.id] = (await api().isProcessOwnerInUse(po.id)).data
//    }
//    setProcessOwnersInUseMap(processOwnersInUse)
// }

export const mapWorkplacesSNIs = (workplaces: Workplace[], snis: SNI[]) => {
   const mappedWorkplaces = workplaces.map((workplace) => {
      return {
         ...workplace,
         snIs:
            workplace.snIs.map((sniConn) => {
               const sni = snis.find((sni) => sni.id === sniConn.sniId)
               return {
                  ...sniConn,
                  sni: sni,
               }
            }) ?? [],
      }
   })
   return mappedWorkplaces
}

export const getWorkplaceUsage = async (filteredItems: any[], setWorkplaceInUseMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>) => {
   const workplacesInUse: Record<string, boolean> = {}
   for (const wp of filteredItems) {
      workplacesInUse[wp.id] = (await api().isWorkplaceInUse(wp.id)).data
   }
   setWorkplaceInUseMap(workplacesInUse)
}

export const getProtectedAssetGoalsByOrganisationId = async (dataContext: IDataContext, setRootState: React.Dispatch<React.SetStateAction<IRootState>>) => {
   const { userAccount } = dataContext.state

   try {
      const result = (await api().getProtectedAssetGoalsByOrganisationId(userAccount.organisationId)).data
      setRootState((prev) => ({
         ...prev,
         protectedAssetGoals: result,
      }))
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchItem', { Item: t('ProtectedAsset/OrganisationalGoal') }))
   }
}

export const getProtectedAssetGoalName = (type: ProtectedAssetGoalType) => {
   switch (type) {
      case ProtectedAssetGoalType.ProtectedAsset:
         return t('ProtectedAsset')
      case ProtectedAssetGoalType.OrganisationalGoal:
         return t('OrganisationalGoal')
   }
}

export const getProtectedAssetsOrganisationalGoalsColumns = (): TableColumnDefinition<ProtectedAssetGoal>[] => [
   {
      columnId: 'name',
      renderHeaderCell: () => t('Name'),
      renderCell: (item) => item.name,
      compare: (a, b) => a.name.localeCompare(b.name),
   },
   {
      columnId: 'description',
      renderHeaderCell: () => t('Description'),
      renderCell: (item) => item.description,
      compare: (a, b) => a.description.localeCompare(b.description),
   },
   {
      columnId: 'valuation',
      renderHeaderCell: () => t('Valuation'),
      renderCell: (item) => {
         return item.valuation || item.valuation === 0 ? (
            formatToSwedishCrowns(item.valuation) + ' kr'
         ) : (
            <Text size={300} italic>
               {t('NoValueSet')}
            </Text>
         )
      },
      compare: (a, b) => a.valuation - b.valuation,
   },
   {
      columnId: 'workplace',
      renderHeaderCell: () => t('WorkplaceAffiliation'),
      renderCell: (item) => item?.workplace?.name,
      compare: (a, b) => a?.workplace?.name.localeCompare(b?.workplace.name),
   },
   {
      columnId: 'process',
      renderHeaderCell: () => t('ProcessAffiliation'),
      renderCell: (item) => item.process,
      compare: (a, b) => a.process.localeCompare(b.process),
   },
]

export const getWorkplacesColumns = (): TableColumnDefinition<Workplace>[] => {
   return [
      {
         columnId: 'cfar',
         renderHeaderCell: () => t('CFAR'),
         renderCell: (item) => item.cfar,
         compare: (a, b) => a.cfar - b.cfar,
      },
      {
         columnId: 'name',
         renderHeaderCell: () => t('Name'),
         renderCell: (item) => item.name,
         compare: (a, b) => a.name.localeCompare(b.name),
      },
      {
         columnId: 'description',
         renderHeaderCell: () => t('WorkplaceDescription'),
         renderCell: (item) => item.description,
         compare: (a, b) => a?.description?.localeCompare(b?.description),
      },
      {
         columnId: 'sni',
         renderHeaderCell: () => t('SNICodes'),
         renderCell: (item) =>
            item.snIs
               ?.slice()
               .sort((a, b) => (a.sni.code ?? 0) - (b.sni.code ?? 0))
               .map((s) => s.sni?.code.toString().padStart(2, '0').padEnd(5, 'xxx'))
               .join(', '),
         compare: (a, b) => {
            const aCodes = a.snIs.map((s) => s.sni.code).join(',')
            const bCodes = b.snIs.map((s) => s.sni.code).join(',')
            return aCodes.localeCompare(bCodes)
         },
      },
      {
         columnId: 'employees',
         renderHeaderCell: () => t('NumberOfEmployees'),
         renderCell: (item) => item?.organisationSize?.text,
         compare: (a, b) => a.organisationSize.sortOrder - b.organisationSize.sortOrder,
      },
   ]
}

export const isValidSettingsTab = (tab: string) =>
   tab === SettingsTabs.ProtectedAssets ||
   tab === SettingsTabs.OrganisationalGoals ||
   tab === SettingsTabs.Workplaces ||
   tab === SettingsTabs.ProcessOwners ||
   tab === SettingsTabs.Roles

export const getWorkplaceOrgSizes = (allOrgSizes: OrganisationSize[], organisation: Organisation) => {
   const orgSizes = allOrgSizes.filter((orgSize) => orgSize?.sortOrder <= organisation.organisationSize?.sortOrder)

   return orgSizes
      .sort((a, b) => a?.sortOrder - b?.sortOrder)
      .map((orgSize) => ({
         key: orgSize.id,
         text: orgSize.text,
      }))
}

export const getWorkplaceSNIs = (organisation: Organisation) => {
   if (organisation.snIs.length === 0) return []
   const mappedOptions: SNIWorkplaceConnection[] = organisation.snIs.map((orgConn) => ({
      sniId: orgConn.sni.id,
      sni: orgConn.sni,
   }))

   return mappedOptions
}

export const getProcessOwnersColumns = (): TableColumnDefinition<ProcessOwnerDto>[] => [
   {
      columnId: 'process',
      renderHeaderCell: () => t('Process'),
      renderCell: (item) => item?.processes?.map((p) => p?.text).join(', ') ?? '',
      compare: (a, b) => a?.processes[0]?.text.localeCompare(b?.processes[0]?.text),
   },
   {
      columnId: 'owner',
      renderHeaderCell: () => t('Owner'),
      renderCell: (item) => <ProcessOwnerAvatarOutput processOwner={item} />,
      compare: (a, b) => {
         const aName = getFullName(a?.userAccount) ?? a.ownerFreeText ?? ''
         const bName = getFullName(b?.userAccount) ?? b.ownerFreeText ?? ''
         return aName.localeCompare(bName)
      },
   },

   // Hidden for now
   // {
   //    columnId: 'workplace',
   //    renderHeaderCell: () => t('Workplace'),
   //    renderCell: (item) => item?.workplaces?.map((w) => w?.name).join(', ') ?? '',
   //    compare: (a, b) => a.workplaces[0].name.localeCompare(b.workplaces[0].name),
   // },
]

export const isSystemAdminOrCreator = (item: any, userAccount: UserAccount) => {
   if (item) {
      if (userAccount.role === Role.SystemAdministrator) return true
      else if (item.createdById === userAccount.id) return true
   }
   return false
}
